import {
  subDays,
  subMonths,
  subYears,
  startOfYear,
  startOfMonth,
  endOfMonth,
  startOfDay,
  endOfDay,
  endOfYear,
  differenceInMonths,
  differenceInYears,
  differenceInCalendarDays,
  differenceInCalendarMonths,
  differenceInCalendarYears,
  isSameDay,
  isSameMonth,
  isSameYear,
  isToday,
  startOfQuarter,
  endOfQuarter,
  subQuarters,
  areIntervalsOverlapping,
  isSameHour,
} from 'date-fns';

import { FetchSessionInsights } from '@/constants/actions';
import {
  SetSessionInsights,
  SetInsightsLoading,
  SetSelectedDomain,
  SetSelectedTimeRange,
  SetLoadingInsightsJson,
  SetLoadingInsightsPdf,
  SetShowInsightsToggleUI,
  SetInsightsSectionsCounts,
  SetSelectedVersion,
  SetCustomRange,
  ResetInsights,
  SetLoadingInsightsAllDataJson,
} from '@/constants/mutations';
import { endpoints } from '@/services/amplify-api/endpoints';
import {
  GetCurrentAmp,
  GetDateRanges,
  GetCurrentDateRange,
  GetAvailableTimeRanges,
  GetInsightsUrlQueryParams,
  GetResponseInsights,
  GetSessionsWithTrends,
  GetDataInterval,
  GetDataByLabels,
  GetTimeRangeLabels,
} from '@/constants/getters';
import {
  getDailyLabels,
  getHourlyLabels,
  getMonthlyLabels,
  getYearlyLabels,
  getWeekDayLabels,
  timeZoneSafeDate,
} from '@/utils/date';
import { getTrendValue } from '@/utils/amp-utils';

const defaultState = () => {
  return {
    insights: {
      sessions: null,
    },
    filters: {
      domain: '',
      version: '-1',
      timeRange: 'lastSevenDays',
      customRange: {
        start: new Date(),
        end: new Date(),
      },
    },
    sectionCounts: {
      responses: 0,
    },
    showToggleUI: {
      responses: true,
      quickStats: false,
    },
    insightsLoading: false,
    loadingAllDataJson: false,
    loadingJson: false,
    loadingPdf: false,
  };
};

export const state = defaultState;

export const mutations = {
  [SetSessionInsights](state, data) {
    state.insights = { ...state.insights, sessions: data };
  },

  [SetSelectedDomain](state, domain) {
    state.filters = { ...state.filters, domain };
  },

  [SetSelectedTimeRange](state, timeRange) {
    state.filters = { ...state.filters, timeRange };
  },

  [SetSelectedVersion](state, version) {
    state.filters = { ...state.filters, version };
  },

  [SetCustomRange](state, range) {
    state.filters = { ...state.filters, customRange: range };
  },

  [SetLoadingInsightsAllDataJson](state, loading) {
    state.loadingAllDataJson = loading;
  },

  [SetInsightsLoading](state, data) {
    state.insightsLoading = data;
  },

  [SetLoadingInsightsPdf](state, loading) {
    state.loadingPdf = loading;
  },

  [SetLoadingInsightsJson](state, loading) {
    state.loadingJson = loading;
  },

  [SetShowInsightsToggleUI](state, data) {
    state.showToggleUI = { ...state.showToggleUI, ...data };
  },

  [SetInsightsSectionsCounts](state, data) {
    state.sectionCounts = { ...state.sectionCounts, ...data };
  },

  [ResetInsights](state) {
    Object.assign(state, defaultState());
  },
};

export const actions = {
  async [FetchSessionInsights]({ commit, getters }, id) {
    try {
      commit(SetInsightsLoading, true);
      const query = getters[GetInsightsUrlQueryParams];

      const url = `${endpoints.insights.session(id).url}${query}`;
      const { data } = await this.$amplifyApi.get({ url });

      commit(SetSessionInsights, data);
    } catch (error) {
      console.error(`[FetchSessionInsights]: ${error}`);
    } finally {
      commit(SetInsightsLoading, false);
    }
  },
};

export const getters = {
  [GetDateRanges]: (_state, _getters, _rootState, rootGetters) => {
    const currentAmp = rootGetters[`ampStore/${GetCurrentAmp}`];
    const currentDate = new Date();
    const firstVersion = currentAmp?.publishedAmps?.find((v) => v.version === 1);

    const ranges = [
      {
        key: 'lastSevenDays',
        default: true,
        label: 'Last 7 days',
        range: { start: startOfDay(subDays(currentDate, 6)), end: endOfDay(currentDate) },
        comparedRange: { start: subDays(currentDate, 13), end: subDays(currentDate, 7) },
        interval: 'DAY',
      },
      {
        key: 'toCurrentDate',
        label: 'To current date',
        range: { start: firstVersion ? startOfDay(new Date(firstVersion.createdAt)) : null, end: endOfDay(currentDate) },
      },
      {
        key: 'custom',
        label: 'Custom date range',
        custom: true,
      },
      {
        key: 'thisMonth',
        preset: true,
        label: 'This month',
        range: { start: startOfMonth(currentDate), end: endOfMonth(currentDate) },
        comparedRange: { start: startOfMonth(subMonths(currentDate, 1)), end: endOfMonth(subMonths(currentDate, 1)) },
        interval: 'DAY',
      },
      {
        key: 'lastMonth',
        preset: true,
        label: 'Last month',
        range: { start: startOfMonth(subMonths(currentDate, 1)), end: endOfMonth(subMonths(currentDate, 1)) },
        comparedRange: { start: startOfMonth(subMonths(currentDate, 2)), end: endOfMonth(subMonths(currentDate, 2)) },
        interval: 'DAY',
      },
      {
        key: 'thisQuarter',
        preset: true,
        label: 'This quarter',
        range: { start: startOfQuarter(currentDate), end: endOfQuarter(currentDate) },
        comparedRange: { start: startOfQuarter(subQuarters(currentDate, 1)), end: endOfQuarter(subQuarters(currentDate, 1)) },
        interval: 'MONTH',
      },
      {
        key: 'lastQuarter',
        preset: true,
        label: 'Last quarter',
        range: { start: startOfQuarter(subQuarters(currentDate, 1)), end: endOfQuarter(subQuarters(currentDate, 1)) },
        comparedRange: { start: startOfQuarter(subQuarters(currentDate, 2)), end: endOfQuarter(subQuarters(currentDate, 2)) },
        interval: 'MONTH',
      },
      {
        key: 'thisYear',
        preset: true,
        label: 'This year',
        range: { start: startOfYear(currentDate), end: endOfYear(currentDate) },
        comparedRange: { start: startOfYear(subYears(currentDate, 1)), end: endOfYear(subYears(currentDate, 1)) },
        interval: 'MONTH',
      },
      {
        key: 'lastYear',
        preset: true,
        label: 'Last year',
        range: { start: startOfYear(subYears(currentDate, 1)), end: endOfYear(subYears(currentDate, 1)) },
        comparedRange: { start: startOfYear(subYears(currentDate, 2)), end: endOfYear(subYears(currentDate, 2)) },
        interval: 'MONTH',
      },
    ];

    return ranges;
  },

  [GetCurrentDateRange]: (state, { [GetDateRanges]: dateRanges }) => {
    const { timeRange, customRange } = state.filters;
    let currentRange = null;
    if (timeRange) {
      currentRange = dateRanges.find((tr) => tr.key === timeRange);
    }
    if (currentRange.key === 'custom') {
      currentRange.range = customRange;
      currentRange.comparedRange = {
        start: subDays(customRange.start, differenceInCalendarDays(customRange.end, customRange.start) + 1),
        end: subDays(customRange.start, 1),
      };
    }
    return currentRange || dateRanges.find((tr) => tr.default);
  },

  [GetAvailableTimeRanges]: (_state, { [GetCurrentDateRange]: currentDateRange, [GetDateRanges]: dateRanges }, _rootState, rootGetters) => {
    const currentAmp = rootGetters[`ampStore/${GetCurrentAmp}`];
    const firstVersion = currentAmp?.publishedAmps?.find((v) => v.version === 1);

    const fromDate = firstVersion ? new Date(firstVersion.createdAt) : currentDateRange?.range?.start;
    const toDate = new Date();
    const dateRange = { start: fromDate, end: toDate };

    return dateRanges.filter((dRange) => {
      const { preset, range } = dRange;
      return !preset || (range?.start && range?.end && areIntervalsOverlapping(dateRange, range));
    });
  },

  [GetInsightsUrlQueryParams]: (state, { [GetCurrentDateRange]: currentDateRange }, _rootState, _rootGetters) => {
    const { domain, timeRange, version } = state.filters;
    const { range } = currentDateRange || {};

    const params = {
      domainId: domain,
      fromDate: range?.start ? timeZoneSafeDate(range.start, timeRange, false).toISOString() : null,
      toDate: range?.end ? timeZoneSafeDate(range.end, timeRange, true).toISOString() : null,
      timeRange,
      version,
    };

    const query = Object.keys(params).reduce(
      (prev, key) => (params[key] ? prev + `${prev ? '&' : '?'}${key}=${params[key]}` : prev),
      ''
    );

    return query;
  },

  [GetResponseInsights]:
    (state) =>
    (isPdf = false) => {
      if (!state.insights.sessions) return [];

      let count = 0;
      const insights = state.insights.sessions.questionInsights.reduce((prev, current) => {
        /**
         * @var chartData will contain the data and labels array
         * in the correct ordder based on the responseCount and responseIndex values.
         */
        const chartData = current.responses.reduce(
          (prev, sessionResponse) => {
            sessionResponse.responses.forEach((r) => {
              prev.labels.push(sessionResponse.responseTexts[r.responseIndex]);
              prev.data.push(r.responseCount);
            });

            return prev;
          },
          { labels: [], data: [] }
        );

        const totalCount = chartData.data.reduce((prev, current) => {
          return (prev += current);
        }, 0);

        count += totalCount;

        /**
         * Sorting the labels and data arrays in ascending order
         * based on the values inside data.
         */
        for (let i = 0; i < chartData.data.length; i++) {
          for (let j = i + 1; j < chartData.data.length; j++) {
            if (chartData.data[j] > chartData.data[i]) {
              let placeholder = chartData.data[i];
              chartData.data[i] = chartData.data[j];
              chartData.data[j] = placeholder;
              placeholder = chartData.labels[i];
              chartData.labels[i] = chartData.labels[j];
              chartData.labels[j] = placeholder;
            }
          }
        }

        prev.push({
          title: current.questionText,
          props: {
            labels: chartData.labels,
            data: chartData.data,
            conclusion: `Total responses: <span>${totalCount}</span>`,
            totalCount,
            animation: isPdf ? { duration: 0 } : {},
          },
          type: chartData.labels.length > 2 ? 'BarsChart' : 'PieChart',
        });

        return prev;
      }, []);

      return { insights, count };
    },

  [GetDataInterval]: (state, { [GetCurrentDateRange]: currentDateRange }) => {
    const { timestampInterval } = state.insights?.sessions?.timeseriesInsights?.[0] || {};
    const { range, interval } = currentDateRange || {};
    let dataInterval = interval || timestampInterval;

    // If the data is for one day only, then we'll ignore the timestampInterval
    if (!interval && range && differenceInCalendarDays(range.end, range.start) === 0) {
      dataInterval = 'HOUR';
    }

    if (!dataInterval) {
      if (differenceInMonths(range?.end, range?.start) < 1) {
        dataInterval = 'DAY';
      } else if (differenceInYears(range?.end, range?.start) < 1) {
        dataInterval = 'MONTH';
      } else {
        dataInterval = 'YEAR';
      }
    }

    return dataInterval;
  },

  [GetDataByLabels]: (state, { [GetDataInterval]: dataInterval }) => (data, labels) => {
    let evaluate;

    switch (dataInterval) {
      case 'HOUR':
        evaluate = isSameHour;
        break;
      case 'DAY':
        evaluate = isSameDay;
        break;
      case 'MONTH':
        evaluate = isSameMonth;
        break;
      case 'YEAR':
        evaluate = isSameYear;
        break;
    }

    const copy = [];

    labels.forEach((label, index) => {
      const dataIndex = data.findIndex((d) => {
        let dataDate = new Date(d.timestampWindow);
        if (dataInterval === 'HOUR') {
          dataDate = new Date(`${new Date(label.isoValue).toISOString().split('T')[0]}T${d.timestampWindow}`);
        }
        return evaluate(dataDate, new Date(label.isoValue));
      });

      copy[index] = dataIndex >= 0 ? data[dataIndex] : 0;
    });

    return copy;
  },

  [GetTimeRangeLabels]: (_state, { [GetCurrentDateRange]: currentDateRange, [GetDataInterval]: dataInterval }) => {
    const { range } = currentDateRange || {};
    let labels = [];

    switch (dataInterval) {
      case 'HOUR':
        labels = getHourlyLabels(range?.start, range?.end);
        break;
      case 'DAY':
        if (differenceInCalendarDays(range?.end, range?.start) < 7 && isToday(range.end)) {
          labels = getWeekDayLabels(range?.start, range?.end);
        } else {
          labels = getDailyLabels(range?.start, range?.end);
        }
        break;
      case 'MONTH':
        labels = getMonthlyLabels(range?.start, differenceInCalendarMonths(range?.end, range?.start));
        break;
      case 'YEAR':
        labels = getYearlyLabels(range?.start, differenceInCalendarYears(range?.end, range?.start));
        break;
    }

    return labels;
  },

  [GetSessionsWithTrends]: (
    state,
    {
      [GetCurrentDateRange]: currentDateRange,
      [GetDataByLabels]: getDataByLabels,
      [GetTimeRangeLabels]: getTimeRangeLabels,
    }
  ) => {
    const { sessions } = state.insights;
    if (!sessions) return;

    const { range } = currentDateRange;
    const { hourlyInsights, timeseriesInsights, trendInsights } = sessions;
    let insightsData = timeseriesInsights;

    if (range && differenceInCalendarDays(range?.end, range?.start) === 0) {
      insightsData = hourlyInsights;
    }

    const sessionsTotals = insightsData.reduce(
      (prev, current) => {
        prev.sessions += current.sessionsTotal;
        prev.uniqueSessions += current.sessionsUnique;

        return prev;
      },
      { sessions: 0, uniqueSessions: 0 }
    );

    let sessionsTrend;
    let uniqueSessionsTrend;
    // Calculate trend based of the last time range sessionTotal value
    if (trendInsights) {
      const lastSessionTotal = trendInsights.sessionsTotal;
      const lastUniqueSessionsTotal = trendInsights.sessionsUnique;

      sessionsTrend = getTrendValue(sessionsTotals.sessions, lastSessionTotal);
      uniqueSessionsTrend = getTrendValue(sessionsTotals.uniqueSessions, lastUniqueSessionsTotal);
    }

    // Sort out data entries based on the selected time range and order by labels
    const labels = getTimeRangeLabels;
    // Reassign data to the numeric values array orderd by the corresponding label
    const data = getDataByLabels(insightsData, labels);

    const sessionsData = data.map((d) => d.sessionsTotal || d);
    const uniqueSessions = data.map((d) => d.sessionsUnique || d);

    return {
      sessionsTrend,
      uniqueSessionsTrend,
      total: sessionsTotals.sessions,
      uniqueTotal: sessionsTotals.uniqueSessions,
      labels: labels.map((l) => l.value),
      sessions: [{ label: 'Sessions', data: sessionsData }],
      uniqueSessions: [{ label: 'Unique sessions', data: uniqueSessions }],
    };
  },
};
